import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Heart, Shield, Clock, Users, MessageCircle, CheckCircle, Star, ArrowRight, Play, X, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

export default function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);

  // Using a reliable sample video - replace with your actual demo video URL
  const demoVideoUrl = "https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4";

  const handlePlayClick = () => {
    setVideoStarted(true);
    setIsPlaying(true);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleCloseModal = (open) => {
    setDemoOpen(open);
    if (!open) {
      setVideoStarted(false);
      setIsPlaying(false);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Demo Video Modal */}
      <Dialog open={demoOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-slate-900 border-slate-700">
          <DialogHeader className="p-4 bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700">
            <DialogTitle className="text-white flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span>ElderLink Demo</span>
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-slate-900 relative">
            {!videoStarted ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-slate-800 to-slate-900">
                <button 
                  onClick={handlePlayClick}
                  className="w-24 h-24 rounded-full bg-teal-500 hover:bg-teal-400 transition-all duration-300 flex items-center justify-center mb-6 shadow-lg shadow-teal-500/30 hover:scale-105 cursor-pointer"
                  data-testid="play-video-btn"
                >
                  <Play className="w-12 h-12 text-white ml-1" fill="white" />
                </button>
                <h3 className="text-2xl font-bold text-white mb-3">See ElderLink in Action</h3>
                <p className="text-slate-400 max-w-md mx-auto text-center mb-6 px-4">
                  Watch how families coordinate care, track caregiver hours, and stay connected.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-300 mb-6 px-4">
                  <span className="flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-full">
                    <CheckCircle className="w-4 h-4 text-teal-500" /> AI Care Guide™
                  </span>
                  <span className="flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-full">
                    <CheckCircle className="w-4 h-4 text-teal-500" /> Care Hours™
                  </span>
                  <span className="flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-full">
                    <CheckCircle className="w-4 h-4 text-teal-500" /> Care Circle™
                  </span>
                </div>
              </div>
            ) : (
              <div className="relative w-full h-full group">
                <video 
                  ref={videoRef}
                  src={demoVideoUrl}
                  className="w-full h-full object-contain bg-black"
                  autoPlay
                  playsInline
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => { setIsPlaying(false); setVideoStarted(false); }}
                />
                {/* Custom Video Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={togglePlay}
                      className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5 text-white" fill="white" />
                      ) : (
                        <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                      )}
                    </button>
                    <button 
                      onClick={toggleMute}
                      className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    >
                      {isMuted ? (
                        <VolumeX className="w-5 h-5 text-white" />
                      ) : (
                        <Volume2 className="w-5 h-5 text-white" />
                      )}
                    </button>
                    <div className="flex-1" />
                    <button 
                      onClick={handleFullscreen}
                      className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    >
                      <Maximize className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-4 bg-slate-800 border-t border-slate-700">
            <div className="flex items-center justify-between">
              <p className="text-slate-400 text-sm">Ready to transform your caregiving experience?</p>
              <Link to="/register" onClick={() => handleCloseModal(false)}>
                <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6">
                  Start Free Trial
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/images/elderlink-logo.webp" alt="ElderLink" className="w-9 h-9 sm:w-10 sm:h-10 object-contain" />
              <span className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">ElderLink</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
            </nav>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/login">
                <Button variant="ghost" className="px-2 sm:px-4 text-slate-700 hover:text-slate-900" data-testid="login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-4 sm:px-6" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium">
                <Shield className="w-4 h-4" />
                Trusted by 10,000+ Families
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight font-['Manrope']">
                Care Coordination Made <span className="text-teal-600">Simple</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                ElderLink helps family caregivers coordinate care, track hours, and connect with trusted independent caregivers—all in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 h-14 text-lg btn-primary" data-testid="hero-cta-btn">
                    Start Free Trial
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 h-14 text-lg border-2 border-slate-200 hover:border-slate-900"
                  data-testid="watch-demo-btn"
                  onClick={() => setDemoOpen(true)}
                >
                  <Play className="w-5 h-5" />
                  Watch Demo
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600">4.9/5 from 2,000+ reviews</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1764751024733-6bfd7783b50d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                  alt="Happy multi-generational family" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 max-w-xs">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">AI Care Guide™</p>
                    <p className="text-sm text-slate-500">24/7 caregiving support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Manrope']">
              Everything You Need for Peace of Mind
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              From AI-powered guidance to time tracking, ElderLink has you covered.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: MessageCircle,
                title: "AI Care Guide™",
                description: "Get instant, personalized caregiving advice from our AI assistant, available 24/7.",
                color: "bg-teal-500"
              },
              {
                icon: Users,
                title: "Care Circle™",
                description: "Coordinate with family members and caregivers. Assign tasks, share notes, stay connected.",
                color: "bg-slate-900"
              },
              {
                icon: Clock,
                title: "Care Hours™",
                description: "Track caregiver shifts with clock in/out, notes, and easy approval workflows.",
                color: "bg-amber-500"
              },
              {
                icon: Shield,
                title: "Trust & Verification",
                description: "Verified caregivers with background checks, reviews, and trust badges.",
                color: "bg-rose-500"
              },
              {
                icon: Heart,
                title: "Stress Tracking",
                description: "Monitor your wellbeing with daily check-ins and personalized support.",
                color: "bg-purple-500"
              },
              {
                icon: CheckCircle,
                title: "Task Management",
                description: "Create, assign, and track caregiving tasks with your entire care team.",
                color: "bg-blue-500"
              }
            ].map((feature, index) => (
              <Card key={index} className="bg-white border-slate-100 hover:shadow-lg transition-shadow duration-300 card-interactive">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 font-['Manrope']">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Manrope']">
              Get Started in Minutes
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Create Your Profile", description: "Set up your care recipient's profile with health conditions, needs, and preferences." },
              { step: "2", title: "Build Your Circle", description: "Invite family members and connect with verified independent caregivers." },
              { step: "3", title: "Coordinate Care", description: "Track hours, manage tasks, and get AI-powered guidance whenever you need it." }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-teal-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 font-['Manrope']">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Manrope']">
              Simple, Transparent Pricing
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <Card className="border-2 border-slate-200">
              <CardContent className="p-4 sm:p-8">
                <h3 className="text-2xl font-bold text-slate-900 font-['Manrope']">Free</h3>
                <p className="text-slate-600 mt-2">For getting started</p>
                <div className="mt-6">
                  <span className="text-2xl sm:text-4xl font-bold text-slate-900">$0</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <ul className="mt-8 space-y-4">
                  {["5 AI messages/day", "Basic care profile", "1 Care Circle member", "View caregivers"].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-500" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button variant="outline" className="w-full mt-8 h-12 rounded-full border-2" data-testid="free-plan-btn">
                    Start Free
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Premium Plan */}
            <Card className="border-2 border-teal-500 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                POPULAR
              </div>
              <CardContent className="p-4 sm:p-8">
                <h3 className="text-2xl font-bold text-slate-900 font-['Manrope']">Premium</h3>
                <p className="text-slate-600 mt-2">For serious caregivers</p>
                <div className="mt-6">
                  <span className="text-2xl sm:text-4xl font-bold text-slate-900">$12.99</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <ul className="mt-8 space-y-4">
                  {["Unlimited AI guidance", "Full care profiles", "Unlimited Care Circle", "Priority caregiver matching", "Time tracking & exports", "24/7 priority support"].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-500" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full mt-8 h-12 rounded-full bg-teal-500 hover:bg-teal-600 text-white" data-testid="premium-plan-btn">
                    Start Premium Trial
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Manrope']">
            Ready to Transform Your Caregiving Journey?
          </h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
            Join thousands of families who trust ElderLink to coordinate care with confidence.
          </p>
          <Link to="/register">
            <Button size="lg" className="mt-8 bg-teal-500 hover:bg-teal-400 text-white rounded-full px-10 h-14 text-lg" data-testid="cta-btn">
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/images/elderlink-logo.webp" alt="ElderLink" className="w-10 h-10 object-contain" />
                <span className="text-xl font-bold text-white font-['Manrope']">ElderLink</span>
              </div>
              <p className="text-slate-400 text-sm">
                Care coordination and caregiver support platform. We are not a medical provider or caregiving agency.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">For Caregivers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">HIPAA Compliance</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400 text-sm">
            <p>&copy; 2024 ElderLink. All rights reserved.</p>
            <p className="mt-2">Emergency? Call 911. We do not provide medical advice.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
